import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from "@clerk/clerk-react";
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    MessageSquare,
    ChevronLeft,
    Send
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster } from "@/components/ui/sonner";
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocuments } from './contexts/DocumentsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatInterface = () => {
    const { documentId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const { documents, prefetchDocuments } = useDocuments();

    // Get document from cache immediately for instant display
    const cachedDocument = documents.find(doc => doc.id === documentId);
    const [document, setDocument] = useState(cachedDocument || null);

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false); // Start with false for instant UI
    const [isLoadingMessages, setIsLoadingMessages] = useState(true); // Track chat history loading
    const [sessionId] = useState(`session-${Date.now()}`);

    // Fetch document details if not in cache
    useEffect(() => {
        const fetchDocument = async () => {
            if (!user || !documentId) return;

            // If we don't have cached document, fetch it
            if (!cachedDocument) {
                try {
                    const response = await axios.get(`${API}/documents/${documentId}?user_id=${user.id}`);
                    setDocument(response.data);
                } catch (error) {
                    console.error('Error fetching document:', error);
                    toast.error('Document not found');
                    navigate('/dashboard');
                }
            }
        };

        fetchDocument();
    }, [documentId, user, cachedDocument, navigate]);

    // Load chat history separately (non-blocking)
    useEffect(() => {
        const fetchChatHistory = async () => {
            if (!user || !documentId) return;

            try {
                const response = await axios.get(`${API}/chat/${documentId}?user_id=${user.id}`);

                const history = response.data.map(msg => ({
                    role: msg.role,
                    content: msg.content
                }));

                // Only add greeting if history is empty
                if (history.length === 0 && user) {
                    setMessages([{
                        role: 'assistant',
                        content: `Hey ${user.firstName || user.username || 'there'}, how can I help you?`
                    }]);
                } else {
                    setMessages(history);
                }
            } catch (error) {
                console.error('Error fetching chat history:', error);
                // Show greeting on error
                if (user) {
                    setMessages([{
                        role: 'assistant',
                        content: `Hey ${user.firstName || user.username || 'there'}, how can I help you?`
                    }]);
                }
            } finally {
                setIsLoadingMessages(false); // Stop loading after fetch completes
            }
        };

        fetchChatHistory();
        // Prefetch documents for faster dashboard navigation
        prefetchDocuments();
    }, [documentId, user, prefetchDocuments]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage = { role: 'user', content: input };
        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const response = await axios.post(`${API}/chat`, {
                document_id: documentId,
                session_id: sessionId,
                message: input,
                user_id: user?.id
            });

            const assistantMessage = { role: 'assistant', content: response.data.response };
            setMessages((prev) => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Chat error:', error);
            toast.error('Failed to get response');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Toaster position="top-center" />
            <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-to-dashboard-btn"
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Back
                        </Button>
                        <div>
                            <h2 className="font-semibold text-slate-900">{document?.filename}</h2>
                            <p className="text-xs text-slate-500">Chat with your document</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8" data-testid="chat-interface">
                <div className="space-y-4 mb-24">
                    {isLoadingMessages ? (
                        // Skeleton loading state
                        <div className="space-y-4">
                            {/* Assistant message skeleton */}
                            <div className="flex justify-start">
                                <div className="max-w-2xl w-2/3">
                                    <Skeleton className="h-20 w-full rounded-lg" />
                                </div>
                            </div>
                            {/* User message skeleton */}
                            <div className="flex justify-end">
                                <div className="max-w-2xl w-1/2">
                                    <Skeleton className="h-16 w-full rounded-lg" />
                                </div>
                            </div>
                            {/* Assistant message skeleton */}
                            <div className="flex justify-start">
                                <div className="max-w-2xl w-3/4">
                                    <Skeleton className="h-24 w-full rounded-lg" />
                                </div>
                            </div>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center py-12">
                            <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <p className="text-slate-500">Start a conversation about your document</p>
                        </div>
                    ) : null}

                    {!isLoadingMessages && messages.map((msg, idx) => (
                        <div
                            key={idx}
                            className={`chat-message flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            data-testid={`message-${idx}`}
                        >
                            <div
                                className={`max-w-2xl px-4 py-3 rounded-lg ${msg.role === 'user'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-100 text-slate-900'
                                    }`}
                            >
                                <div className="prose prose-sm max-w-none dark:prose-invert">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            p: ({ node, ...props }) => <p className={`mb-2 last:mb-0 ${msg.role === 'user' ? 'text-white' : 'text-slate-900'}`} {...props} />,
                                            ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2 space-y-1" {...props} />,
                                            ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2 space-y-1" {...props} />,
                                            li: ({ node, ...props }) => <li className="mb-0.5" {...props} />,
                                            h1: ({ node, ...props }) => <h1 className="text-lg font-bold mb-2" {...props} />,
                                            h2: ({ node, ...props }) => <h2 className="text-base font-bold mb-2" {...props} />,
                                            h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1" {...props} />,
                                            strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                                        }}
                                    >
                                        {msg.content}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        </div>
                    ))}

                    {loading && (
                        <div className="flex justify-start">
                            <div className="bg-slate-100 px-4 py-3 rounded-lg">
                                <p className="text-slate-600">Thinking...</p>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <div className="border-t border-slate-200 bg-white sticky bottom-0">
                <div className="max-w-5xl mx-auto px-6 py-4">
                    <div className="flex gap-2">
                        <Input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            placeholder="Ask a question about your document..."
                            disabled={loading}
                            data-testid="chat-input"
                            className="flex-1"
                        />
                        <Button
                            onClick={handleSend}
                            disabled={loading || !input.trim()}
                            data-testid="send-message-btn"
                            className="bg-blue-600 text-white hover:bg-blue-700"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatInterface;
